import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  Check,
  ArrowRight,
  Users,
  FileText,
  ExternalLink,
  Crown,
  Sparkles,
  Building,
  AlertCircle,
  Clock,
  Globe,
} from 'lucide-react';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

interface PlanConfig {
  id: string;
  name: string;
  limits: {
    articleLimit: number;
    domainLimit: number;
    removeBranding: boolean;
    includedSeats: number;
    orchestrationLimit: number;
    features: string[];
  };
  pricing: {
    monthly: number;
    annual: number;
  } | null;
  isContactUs: boolean;
}

interface SubscriptionDetails {
  plan: string;
  planName: string;
  features: string[];
  subscription: {
    status: string;
    interval: 'monthly' | 'annual' | null;
    currentPeriodEnd: string | null;
    stripeSubscriptionId: string | null;
  };
  seats: {
    included: number;
    additional: number;
    total: number;
    used: number;
    available: number;
  };
  limits: {
    articles: number;
    removeBranding: boolean;
  };
  trial: {
    isTrialing: boolean;
    endsAt: string | null;
  };
}

export default function BillingTab() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<'monthly' | 'annual'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  const getAuthHeaders = (): HeadersInit => {
    const tokens = localStorage.getItem('auth_tokens');
    if (!tokens) {
      return { 'Content-Type': 'application/json' };
    }
    const { accessToken } = JSON.parse(tokens);
    return {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };
  };

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setError(null);
      const [plansRes, subRes] = await Promise.all([
        fetch(`${API_URL}/api/billing/plans`),
        fetch(`${API_URL}/api/billing/subscription`, { headers: getAuthHeaders() }),
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData.plans || []);
      } else {
        console.error('Failed to load plans:', plansRes.status);
      }

      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
        if (subData?.subscription?.interval) {
          setSelectedInterval(subData.subscription.interval);
        }
      } else {
        console.error('Failed to load subscription:', subRes.status);
        if (subRes.status === 401) {
          setError('Please log in to view billing information.');
        } else {
          setError('Failed to load subscription details. Please try again.');
        }
      }
    } catch (err) {
      console.error('Failed to load billing data:', err);
      setError('Failed to load billing information. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (planId: string) => {
    setCheckoutLoading(planId);
    try {
      const response = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          plan: planId,
          interval: selectedInterval,
        }),
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to start checkout');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/billing/portal`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to open billing portal');
      }
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open billing portal');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (dateStr: string | null) => {
    if (!dateStr) return 0;
    const end = new Date(dateStr);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'starter':
        return <Sparkles className="w-5 h-5" />;
      case 'pro':
        return <Crown className="w-5 h-5" />;
      case 'team':
        return <Crown className="w-5 h-5 text-amber-500" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={() => loadBillingData()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Safety check - if subscription failed to load without setting error
  if (!subscription) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
        <p className="text-yellow-700">{t('billing.unableToLoadSubscription')}</p>
        <button
          onClick={() => loadBillingData()}
          className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  const isTrialExpired =
    subscription?.trial.isTrialing &&
    subscription?.trial.endsAt &&
    new Date(subscription.trial.endsAt) < new Date();

  const trialDaysRemaining = subscription?.trial.endsAt
    ? getDaysRemaining(subscription.trial.endsAt)
    : 0;

  return (
    <div className="space-y-6">
      {/* Trial/Subscription Status Banner */}
      {subscription?.trial.isTrialing && !isTrialExpired && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">
              {t('billing.trialActive', { days: trialDaysRemaining })}
            </h3>
            <p className="text-sm text-amber-700 mt-1">
              {t('billing.trialEnds', { date: formatDate(subscription.trial.endsAt) })}
            </p>
          </div>
        </div>
      )}

      {isTrialExpired && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">{t('billing.trialExpired')}</h3>
            <p className="text-sm text-red-700 mt-1">{t('billing.trialExpiredMessage')}</p>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      {subscription && subscription.subscription.status === 'active' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('billing.currentPlan')}</h3>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
              {t('billing.active')}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              {getPlanIcon(subscription.plan)}
            </div>
            <div>
              <h4 className="text-xl font-bold">{subscription.planName}</h4>
              <p className="text-sm text-gray-500">
                {subscription.subscription.interval === 'annual'
                  ? t('billing.billedAnnually')
                  : t('billing.billedMonthly')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">{t('billing.teamMembers')}</p>
              <p className="text-lg font-semibold">
                {subscription.seats.used} / {subscription.seats.total}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">{t('billing.articles')}</p>
              <p className="text-lg font-semibold">
                {subscription.limits.articles === -1 ? t('billing.unlimited') : subscription.limits.articles}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">{t('billing.branding')}</p>
              <p className="text-lg font-semibold">
                {subscription.limits.removeBranding ? t('billing.removable') : t('billing.required')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase">{t('billing.renewsOn')}</p>
              <p className="text-lg font-semibold">
                {formatDate(subscription.subscription.currentPeriodEnd)}
              </p>
            </div>
          </div>

          <button
            onClick={handleManageSubscription}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <ExternalLink className="w-4 h-4" />
            {t('billing.manageSubscription')}
          </button>
        </div>
      )}

      {/* Billing Interval Toggle */}
      <div className="flex items-center justify-center gap-4 bg-white rounded-lg border border-gray-200 p-4">
        <button
          onClick={() => setSelectedInterval('monthly')}
          className={`px-6 py-2 rounded-lg font-medium transition ${
            selectedInterval === 'monthly'
              ? 'bg-black text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('billing.monthly')}
        </button>
        <button
          onClick={() => setSelectedInterval('annual')}
          className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
            selectedInterval === 'annual'
              ? 'bg-black text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {t('billing.annual')}
          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
            {t('billing.save2Months')}
          </span>
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans
          .filter((p) => p.id !== 'enterprise')
          .map((plan) => {
            const isCurrentPlan = subscription?.plan === plan.id;
            const price = plan.pricing
              ? selectedInterval === 'annual'
                ? plan.pricing.annual
                : plan.pricing.monthly
              : null;

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-lg border-2 p-6 transition ${
                  isCurrentPlan ? 'border-black' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  {getPlanIcon(plan.id)}
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  {isCurrentPlan && (
                    <span className="ml-auto text-xs bg-black text-white px-2 py-1 rounded-full">
                      {t('billing.current')}
                    </span>
                  )}
                </div>

                {price !== null && (
                  <div className="mb-6">
                    <span className="text-3xl font-bold">${price}</span>
                    <span className="text-gray-500">
                      /{selectedInterval === 'annual' ? t('billing.year') : t('billing.month')}
                    </span>
                    {selectedInterval === 'annual' && (
                      <p className="text-sm text-green-600 mt-1">
                        ${Math.round(price / 12)}/{t('billing.month')} {t('billing.billedAnnually')}
                      </p>
                    )}
                  </div>
                )}

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>
                      {plan.limits.includedSeats} {t('billing.teamMembersIncluded')}
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <span>
                      {plan.limits.domainLimit === -1
                        ? t('billing.unlimitedDomains')
                        : `${plan.limits.domainLimit} ${plan.limits.domainLimit === 1 ? t('billing.domain') : t('billing.domains')}`}
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span>
                      {plan.limits.articleLimit === -1
                        ? t('billing.unlimitedArticles')
                        : `${plan.limits.articleLimit} ${t('billing.pagesDocuments')}`}
                    </span>
                  </li>
                  {plan.limits.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <button
                    disabled
                    className="w-full py-2 px-4 bg-gray-100 text-gray-500 rounded-lg font-medium"
                  >
                    {t('billing.currentPlan')}
                  </button>
                ) : (
                  <button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={checkoutLoading === plan.id}
                    className="w-full py-2 px-4 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {checkoutLoading === plan.id ? (
                      t('common.loading')
                    ) : (
                      <>
                        {subscription?.plan === 'trial' || !subscription?.subscription.stripeSubscriptionId
                          ? t('billing.subscribe')
                          : t('billing.upgrade')}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
      </div>

      {/* Enterprise Card */}
      {plans.find((p) => p.id === 'enterprise') && (
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building className="w-5 h-5" />
                <h3 className="text-lg font-semibold">{t('billing.enterprise')}</h3>
              </div>
              <p className="text-gray-300 mb-4">{t('billing.enterpriseDescription')}</p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  {t('billing.unlimitedEverything')}
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  {t('billing.customIntegrations')}
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  {t('billing.dedicatedSupport')}
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400" />
                  {t('billing.sla')}
                </li>
              </ul>
            </div>
          </div>
          <a
            href="mailto:sales@tahc.ai?subject=Tahc%20Enterprise%20Inquiry"
            className="inline-flex items-center gap-2 px-6 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition"
          >
            {t('billing.contactSales')}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}

      {/* Additional Team Members */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">{t('billing.additionalSeats')}</h3>
        <p className="text-gray-600 mb-4">{t('billing.additionalSeatsDescription')}</p>
        <div className="flex items-center gap-4">
          <div className="bg-gray-50 rounded-lg p-4 flex-1">
            <p className="text-2xl font-bold">
              $29<span className="text-sm font-normal text-gray-500">/{t('billing.month')}</span>
            </p>
            <p className="text-sm text-gray-500">{t('billing.perAdditionalMember')}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 flex-1">
            <p className="text-2xl font-bold">
              $290<span className="text-sm font-normal text-gray-500">/{t('billing.year')}</span>
            </p>
            <p className="text-sm text-gray-500">{t('billing.perAdditionalMemberAnnual')}</p>
          </div>
        </div>
        {subscription?.subscription.stripeSubscriptionId && (
          <button
            onClick={handleManageSubscription}
            className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Users className="w-4 h-4" />
            {t('billing.addTeamMembers')}
          </button>
        )}
      </div>
    </div>
  );
}
